import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Download, RefreshCw, Save, Share2 } from 'lucide-react';
import { validateInvoice } from '../../lib/validate';
import { toast } from 'sonner';
import { local } from '../../lib/storage';
import { apiClient, formatApiError } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import InvoiceShareDialog from '../InvoiceShareDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : Math.random().toString(36).slice(2) + Date.now().toString(36);

const bytesToBase64 = (bytes) => {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};

export default function ActionButtons({ invoice, onSaved, onReset }) {
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareConfirmOpen, setShareConfirmOpen] = useState(false);
  const [shareInvoice, setShareInvoice] = useState(null);
  const { user, isPro, refresh } = useAuth();
  const { t } = useLanguage();

  const validateBeforeGenerate = () => {
    const errors = validateInvoice(invoice);
    if (errors.length > 0) {
      errors.forEach((e) => toast.error(e));
      return false;
    }
    return true;
  };

  const saveInvoice = async (status) => {
    let savedInvoice = invoice;
    let savedMeta = null;
    const meta = {
      id: uid(),
      bj_key: invoice.bj_key,
      number: invoice.number,
      date: invoice.date,
      customerName: invoice.customer.name,
      grandTotal: invoice.taxSummary.grandTotal,
      created_at: new Date().toISOString(),
    };
    if (user) {
      const payload = status ? { ...invoice, status } : invoice;
      const { data } = await apiClient.post('/invoices', payload);
      savedInvoice = data;
      if (status === 'sent' && data.id && (data.status || 'draft') === 'draft') {
        const updated = await apiClient.patch(`/invoices/${data.id}/status`, { status: 'sent' });
        savedInvoice = updated.data;
      }
      savedMeta = { ...meta, bj_key: savedInvoice.bj_key || meta.bj_key, number: savedInvoice.number, full: savedInvoice };
      await refresh();
    }
    return { savedInvoice, savedMeta, meta };
  };

  const handleSaveOnly = async () => {
    if (!user || user === false) {
      toast.info(t('loginSaveHistoryNotice'));
      return;
    }
    if (!validateBeforeGenerate()) return;

    setBusy(true);
    try {
      await saveInvoice('draft');
      toast.success(t('invoiceSaved'));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!validateBeforeGenerate()) return;

    setBusy(true);
    try {
      let savedInvoice;
      let savedMeta;
      let meta;
      try {
        ({ savedInvoice, savedMeta, meta } = await saveInvoice());
      } catch (e) {
        toast.error(formatApiError(e));
        return;
      }
      const { generateInvoicePDF, downloadPDF } = await import('../../lib/pdf');
      const shouldWatermark = !isPro;
      const bytes = await generateInvoicePDF(savedInvoice, { watermark: shouldWatermark });
      if (user && isPro && savedInvoice.id) {
        try {
          await apiClient.put(`/invoices/${savedInvoice.id}/pdf`, { content_base64: bytesToBase64(bytes) });
        } catch {
          toast.error(t('pdfCloudStorageFailed'));
        }
      }
      downloadPDF(bytes, `${savedInvoice.number || 'Invoice'}.pdf`);
      if (user) {
        if (!isPro) toast.info(t('watermarkNotice'));
      } else {
        toast.info(t('loginSaveHistoryNotice'));
      }
      local.incrementCounter();
      if (onSaved) onSaved(savedMeta || meta);
      toast.success(t('invoiceDownloaded'));
      // GA4 custom event
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'download_pdf', {
          invoice_number: invoice.number || '',
          grand_total: invoice.taxSummary?.grandTotal || 0,
          is_inter_state: invoice.taxSummary?.isInterState ? 'yes' : 'no',
          customer_state: invoice.customer?.state || '',
        });
      }
    } catch (e) {
      console.error(e);
      toast.error(t('pdfGenerateFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleShare = () => {
    if (!validateBeforeGenerate()) return;
    setShareConfirmOpen(true);
  };

  const confirmShare = async () => {
    setShareConfirmOpen(false);
    setBusy(true);
    try {
      const { savedInvoice } = await saveInvoice('sent');
      setShareInvoice(savedInvoice);
      setShareOpen(true);
      local.incrementCounter();
      if (onSaved) onSaved();
      if (onReset) onReset();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="action-buttons">
      <div className="grid grid-cols-1 gap-3 p-4 bg-white border border-slate-200 rounded-xl shadow-sm sm:grid-cols-2">
        <Button onClick={handleSaveOnly} disabled={busy} className="h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold" data-testid="save-invoice-btn">
          <Save className="w-4 h-4 mr-2" />
          {busy ? t('saving') : t('saveInvoice')}
        </Button>
        <Button
          onClick={handleDownload}
          disabled={busy}
          variant="outline"
          className="h-12 font-bold"
          data-testid="download-pdf-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {busy ? t('generating') : t('downloadPdf')}
        </Button>
        <Button variant="outline" onClick={handleShare} disabled={busy} className="h-12 font-bold" data-testid="share-invoice-btn">
          <Share2 className="w-4 h-4 mr-2" /> {busy ? t('preparing') : t('share')}
        </Button>
        <Button variant="ghost" onClick={onReset} className="h-12 font-bold" data-testid="reset-invoice-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> {t('newInvoice')}
        </Button>
      </div>
      <p className="text-[11px] text-slate-400 leading-relaxed px-1">
        {t('invoiceDisclaimer')}
      </p>
      <AlertDialog open={shareConfirmOpen} onOpenChange={setShareConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('markSentTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('markSentActionBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('keepEditing')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmShare} className="bg-slate-900 hover:bg-slate-800 text-white">
              {t('confirmAndShare')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <InvoiceShareDialog invoice={shareInvoice || invoice} open={shareOpen} onOpenChange={setShareOpen} />
    </div>
  );
}
